import type { PolicyTokenTransferCondition } from '@mls-wallet/shared-types';
import { toRawTokenUnits } from '@/utils/tokenAmountUtils';
import type { PolicyFormState } from './policy-form2';

type TokenTransferFormData = Pick<
  PolicyFormState,
  'tokenSelection' | 'amountThreshold' | 'destinationAddressSelection'
>;

export function buildPolicyTokenTransferCondition(
  formData: TokenTransferFormData
): PolicyTokenTransferCondition | null {
  const token =
    formData.tokenSelection.type === 'specific'
      ? formData.tokenSelection.token
      : undefined;
  const destinationAddresses =
    formData.destinationAddressSelection.type === 'custom'
      ? (formData.destinationAddressSelection.addresses ?? []).filter(Boolean)
      : [];
  const normalizedDestinationAddresses = new Set(
    destinationAddresses.map((address) => address.toLowerCase())
  );
  const destinationAddressEntries =
    formData.destinationAddressSelection.type === 'custom'
      ? (formData.destinationAddressSelection.entries ?? []).filter((entry) =>
          normalizedDestinationAddresses.has(entry.address.toLowerCase())
        )
      : [];

  const hasTokenRestriction = Boolean(token);
  const hasAmountRestriction =
    hasTokenRestriction && formData.amountThreshold.trim() !== '';
  const hasDestinationRestriction = destinationAddresses.length > 0;

  if (
    !hasTokenRestriction &&
    !hasAmountRestriction &&
    !hasDestinationRestriction
  ) {
    // Leave the condition unset when the policy is still "any token, any amount, any destination".
    return null;
  }

  return {
    // Destination-only policies intentionally keep token metadata null.
    tokenId: token?.id ?? null,
    tokenAddress: token?.address ?? null,
    tokenSymbol: token?.symbol ?? null,
    tokenName: token?.name ?? null,
    tokenLogoUrl: token?.logoUrl ?? null,
    tokenDecimals: token?.decimals ?? null,
    amountThreshold:
      hasAmountRestriction && token
        ? toRawTokenUnits(formData.amountThreshold, token.decimals)
        : null,
    destinationAddresses,
    destinationAddressEntries,
  };
}
